import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CalendarRange, FileDown, Loader2 } from 'lucide-react';
import { PaymentMethodChips } from './PaymentMethodChips';
import { fetchBillingSettings, fetchNextInvoiceNumber, openInvoicePdf } from './billingApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `€${(Number(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const keyOf = (g) => g.customer_id || g.customer_name || 'sin-cliente';

// One invoice per customer with the published, not invoiced orders of the chosen month
export const MonthlyBatchDialog = ({ open, onOpenChange, groups = [], onDone }) => {
  const [month, setMonth] = useState(thisMonth());
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [settings, setSettings] = useState(null);
  const [methods, setMethods] = useState([]);
  const [chosen, setChosen] = useState({});
  const [ivaBy, setIvaBy] = useState({});
  const [nextNumber, setNextNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    fetchBillingSettings().then((s) => { setSettings(s); setMethods(s.payment_methods.filter((m) => m.default).map((m) => m.id)); });
  }, [open]);
  useEffect(() => { if (open && invoiceDate) fetchNextInvoiceNumber(invoiceDate).then(setNextNumber).catch(() => {}); }, [open, invoiceDate]);

  const rows = useMemo(() => groups.map((g) => {
    const orders = g.orders.filter((o) => (o.pub_date || o.order_date || '').slice(0, 7) === month);
    return { g, orders, total: orders.reduce((s, o) => s + (Number(o.pvp) || 0), 0) };
  }).filter((r) => r.orders.length), [groups, month]);

  useEffect(() => {
    setChosen(Object.fromEntries(rows.map((r) => [keyOf(r.g), !!(r.g.company || r.g.customer_name)])));
    setIvaBy(Object.fromEntries(rows.map((r) => [keyOf(r.g), r.g.suggested_iva])));
  }, [rows]);

  const selectedRows = rows.filter((r) => chosen[keyOf(r.g)]);
  const create = async () => {
    setBusy(true);
    try {
      const invoices = selectedRows.map(({ g, orders, total }) => {
        const iva = Number(ivaBy[keyOf(g)]) || 0;
        const base = Math.round(total * 100) / 100;
        return {
          auto_number: true,
          invoice_date: new Date(invoiceDate).toISOString(),
          client_name: g.company || g.customer_name,
          client_nif: g.nif || '',
          client_address: g.billing_address || '',
          concept: settings?.default_concept || '',
          payment_method_ids: methods,
          base_amount: base, iva, irpf: 0,
          total: Math.round(base * (1 + iva / 100) * 100) / 100,
          status: 'A la espera de cobro',
          description: orders.map((o) => `#${o.order_number} ${o.published_url || o.product_url || ''}`.trim()).join(' · '),
          order_lines: orders.map((o) => ({ order_id: o.id, order_number: o.order_number, pvp: Number(o.pvp) || 0, pc: Number(o.pc) || 0, amount: Number(o.pvp) || 0 })),
        };
      });
      const r = await axios.post(`${API}/admin/income-invoices/batch`, { invoices });
      setResult(r.data);
      if (r.data.created.length) toast.success(`${r.data.created.length} ${r.data.created.length === 1 ? 'factura creada' : 'facturas creadas'}`);
      onDone && onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Error al crear las facturas');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="monthly-batch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarRange className="h-5 w-5 text-primary" />Facturas del mes</DialogTitle>
          <DialogDescription>Una factura por cliente con sus pedidos publicados en el mes y aún sin factura.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-2" data-testid="monthly-batch-result">
            {result.created.map((c) => (
              <div key={c.id} className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm">
                <span><b>{c.invoice_number}</b> · {c.client_name}</span>
                <span className="flex items-center gap-3">
                  <b className="text-emerald-700">{eur(c.total)}</b>
                  <Button size="sm" variant="outline" onClick={() => openInvoicePdf(c.id, `${c.invoice_number}_${c.client_name}.pdf`)} data-testid={`batch-pdf-${c.invoice_number}`}>
                    <FileDown className="h-4 w-4 mr-1" />PDF
                  </Button>
                </span>
              </div>
            ))}
            {result.errors.map((e, i) => <p key={i} className="text-sm text-rose-600">{e.client_name}: {e.error}</p>)}
            <div className="flex justify-end pt-2"><Button onClick={() => onOpenChange(false)} data-testid="monthly-batch-close">Cerrar</Button></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Mes de publicación</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="monthly-batch-month" /></div>
              <div><Label>Fecha de las facturas</Label><Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="monthly-batch-date" /></div>
              <div><Label>Primera factura</Label><p className="h-10 flex items-center font-bold text-primary" data-testid="monthly-batch-next-number">{nextNumber}</p></div>
            </div>
            <div>
              <Label className="mb-1.5 block">Métodos de pago en las facturas</Label>
              <PaymentMethodChips methods={settings?.payment_methods || []} selected={methods} onChange={setMethods} testPrefix="batch-pm" />
            </div>
            <div className="border rounded-lg divide-y">
              {rows.length === 0 && <p className="p-4 text-sm text-muted-foreground">No hay pedidos publicados sin factura en ese mes.</p>}
              {rows.map(({ g, orders, total }) => {
                const k = keyOf(g);
                const noName = !(g.company || g.customer_name);
                return (
                  <div key={k} className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid={`batch-row-${k}`}>
                    <Checkbox checked={!!chosen[k]} disabled={noName} onCheckedChange={(v) => setChosen((p) => ({ ...p, [k]: !!v }))} data-testid={`batch-check-${k}`} />
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-medium text-sm">{g.company || g.customer_name || '(sin cliente — asígnalo antes)'}</p>
                      <p className="text-xs text-muted-foreground">{g.nif || 'Sin NIF'} · {orders.length} pedidos</p>
                    </div>
                    <label className="flex items-center gap-1 text-xs">IVA
                      <Input type="number" className="h-8 w-16" value={ivaBy[k] ?? 21} onChange={(e) => setIvaBy((p) => ({ ...p, [k]: e.target.value }))} data-testid={`batch-iva-${k}`} />%
                    </label>
                    <b className="text-emerald-700 w-28 text-right text-sm">{eur(total)}</b>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!selectedRows.length || busy} onClick={create} data-testid="monthly-batch-create">
                {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Crear {selectedRows.length} {selectedRows.length === 1 ? 'factura' : 'facturas'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MonthlyBatchDialog;
